/* ------------------------------------------ Ready and Init Functions ------------------------------ */

// init Functions

function initInjectMissingTokens() {
  $inp = $("<input />").attr({
    type: "hidden",
    name: "token",
    value: g.csrfToken,
  });

  $("form:not(:has(input[name=token]))").prepend($inp);
}

/**
 * Set the left menu for open/closed/automatic mode
 *
 * @param {String} type - menuAutomatic, menuClosed, menuOpen
 * @return {void} - fire off init and update methods to act on the new setting
 */
function modifyMenu(type) {
  $("body").attr("id", type);
  if (g.clientSettingsObj) {
    g.clientSettingsObj.menuDisplay = type;
  }
  initTableScrolling();
  updateClientSetting("MENU_DISPLAY", type);
  initPageTop();
  syncLeftMenuLayout(type === "menuAutomatic");
}

/**
 * Left menu: Automatic mode vs viewport width (TIM-2686, Quotes-only viewport rule)
 *
 * History (do not regress):
 * - Pre-Quotes: MENU_DISPLAY "Automatic" only reflected saved MENU_MODE (standard vs icon). No viewport logic.
 * - TIM-2686 (Quotes v1.1.158): Added auto-compact when window.innerWidth < 1680 in Automatic mode so the
 *   Quotes React app (page=Quotes) has enough horizontal space. Also fixed inverted MENU_MODE on #menuCompactor.
 * - Side effect: Windows display scaling (e.g. 1920px @ 150% → ~1280 CSS px) forced compact on every page,
 *   which users reported as the side menu "suddenly collapsing" outside Quotes (CS, Oct 2026).
 * - Follow-up: Viewport auto-compact in Automatic mode is limited to the Quotes shell only. All other pages
 *   restore pre-TIM-2686 Automatic behavior (saved MENU_MODE only). Open/Closed never use viewport compact.
 *
 * Related: $xLargeBreakpoint 1200px in SCSS is a separate concern (hamburger / hide sidemenu), not this JS.
 * Quotes shell: www/system/default.cfm page=Quotes → LoggedIn section="Quotes", mount #timesheet-quote-root
 * (ApplicationStartUpService config apps.quotes.mount_element_id — keep IDs in sync if that changes).
 */
/** Quotes mount id; must match APPLICATION.CONFIG apps.quotes.mount_element_id. */
const QUOTES_SHELL_MOUNT_ELEMENT_ID = "timesheet-quote-root";
/**
 * Viewport width at/above which Automatic mode may use saved MENU_MODE on Quotes; below, menu starts compact.
 * 1680 ≈ 1920 logical width minus 240px standard sidemenu (_Framework.scss #sidemenu).
 */
const LEFT_MENU_COMPACT_BREAKPOINT_PX = 1680;
/** Last width synced for Automatic resize handling on Quotes (not used for Open/Closed or non-Quotes Automatic). */
let leftMenuLastSyncedWidth = null;

function getMenuDisplayPreference() {
  const bodyId = document.body.id;
  if (bodyId === "menuOpen" || bodyId === "menuClosed" || bodyId === "menuAutomatic") {
    return bodyId;
  }
  return (g.clientSettingsObj && g.clientSettingsObj.menuDisplay) || "menuAutomatic";
}

function isSavedMenuModeCompact() {
  return !!(g.clientSettingsObj && g.clientSettingsObj.menuMode === "icon");
}

/**
 * True when the current page is the logged-in Quotes shell (React app), not standalone quote review/PDF views.
 * Used to scope TIM-2686 viewport compacting — see block comment above LEFT_MENU_COMPACT_BREAKPOINT_PX.
 *
 * @returns {boolean}
 */
function isQuotesShellPage() {
  if (typeof g !== "undefined" && g.section === "Quotes") {
    return true;
  }
  if (document.body && document.body.getAttribute("data-section") === "Quotes") {
    return true;
  }
  return document.getElementById(QUOTES_SHELL_MOUNT_ELEMENT_ID) !== null;
}

function closeAllCompactSecondaryMenus() {
  $("#sidemenu.compact .menu-secondary").slideUp(200);
  $(".dropdown-handler").find(".rotate").removeClass("down");
}

function applyLeftMenuCompactMode(isCompact) {
  const $sidemenu = $("#sidemenu");
  if (!$sidemenu.length) return;

  $sidemenu.toggleClass("compact", isCompact);
  $sidemenu.toggleClass("standard", !isCompact);
  $(".loginButton.clockInOutButton").toggleClass("compact", isCompact);
  $(".loginButton.btnDisabled").toggleClass("compact", isCompact);
  $("#clock").toggleClass("compact", isCompact);
  $(".dropdown-handler").toggleClass("compact", isCompact);
  $(".compactorIcon").toggleClass("rotated", isCompact);

  if (isCompact) {
    closeAllCompactSecondaryMenus();
  }
}

/**
 * Apply MENU_MODE width (compact vs standard).
 *
 * Automatic on Quotes: also reacts to viewport width (TIM-2686, see LEFT_MENU_COMPACT_BREAKPOINT_PX comment).
 * Automatic elsewhere: saved MENU_MODE only (legacy behavior before Quotes viewport rule).
 *
 * @param {boolean} [reapplyAutomaticViewportRules=false] Pass true when user switches to Automatic
 *   so Quotes breakpoint rules run as on first page load.
 */
function syncLeftMenuLayout(reapplyAutomaticViewportRules) {
  if (!$("#sidemenu").length) return;

  const width = window.innerWidth;
  const menuDisplay = getMenuDisplayPreference();

  if (menuDisplay === "menuOpen" || menuDisplay === "menuClosed") {
    leftMenuLastSyncedWidth = width;
    applyLeftMenuCompactMode(isSavedMenuModeCompact());
    return;
  }

  // menuAutomatic outside Quotes: do not force compact by viewport (CS regression after TIM-2686 went global).
  // Automatic mode applies innerWidth breakpoint compacting only on Quotes (TIM-2686).
  if (!isQuotesShellPage()) {
    leftMenuLastSyncedWidth = width;
    applyLeftMenuCompactMode(isSavedMenuModeCompact());
    return;
  }

  if (reapplyAutomaticViewportRules) {
    leftMenuLastSyncedWidth = null;
  }

  const isNarrow = width < LEFT_MENU_COMPACT_BREAKPOINT_PX;
  const isInitial = leftMenuLastSyncedWidth === null;
  const crossedIntoNarrow =
    !isInitial && leftMenuLastSyncedWidth >= LEFT_MENU_COMPACT_BREAKPOINT_PX && isNarrow;
  leftMenuLastSyncedWidth = width;

  if (isNarrow && (isInitial || crossedIntoNarrow)) {
    applyLeftMenuCompactMode(true);
    return;
  }

  if (!isNarrow) {
    applyLeftMenuCompactMode(isSavedMenuModeCompact());
  }
}

/**
 * Sets the global table size
 *
 * @param {String} type - regularFormat, mediumFormat, smallFormat
 * @return void - attaches the type name as a top level class effecting all tables
 */
function modifyTableSize(type) {
  $("body").removeClass();
  $("body").addClass(type);
  updateClientSetting("TABLE_DISPLAY", type);
}

/**
 * Sets the table sorting functionality
 *
 * @param {String} type - individual, grouped
 * @return void - attaches the type name as a top level class effecting all tables
 */
function modifyTableSorting(type) {
  // set data setting
  g.clientSettingsObj.tableSorting = type;
  // update client
  updateClientSetting("TABLE_SORTING", type);
  // reload page w/purposeful timeout to allow client setting to update
  setTimeout(() => {
    location.reload();
  }, 500);
}

/**
 * Sets the table sorting functionality
 *
 * @param {String} state - true, false
 * @return void - save and reload the page
 */
function modifyReportHeaderWrapping(state) {
  // set data setting
  g.clientSettingsObj.reportHeaderWrapping = state;
  console.log("modifyReportHeaderWrapping(), REPORT_HEADER_WRAPPING, state = ", state);
  // update client
  updateClientSetting("REPORT_HEADER_WRAPPING", state);
  // reload page w/purposeful timeout to allow client setting to update
  setTimeout(() => {
    location.reload();
  }, 500);
}

/**
 * Sets the item selection functionality
 *
 * @param {String} type - checkbox, list
 * @return void
 */
function modifyItemSelection(type) {
  // set data setting
  g.clientSettingsObj.itemSelection = type;
  // update client
  updateClientSetting("ITEM_SELECTION", type);
  // reload page w/purposeful timeout to allow client setting to update
  setTimeout(() => {
    location.reload();
  }, 500);
}

/**
 * Sets the item selection ordering
 *
 * @param {String} type - alphabetical, mostrecent
 * @return void
 */
function modifyItemOrdering(type) {
  // set data setting
  g.clientSettingsObj.itemOrdering = type;
  // update client
  updateClientSetting("ITEM_ORDERING", type);
  // reload page w/purposeful timeout to allow client setting to update
  setTimeout(() => {
    location.reload();
  }, 500);
}

/**
 * Sets the user selection functionality
 *
 * @param {String} type - supervisor, alphabetical
 * @return void
 */
function modifyUserSelection(type) {
  // set data setting
  g.clientSettingsObj.userSelection = type;
  // update client
  updateClientSetting("USER_SELECTION", type);
  // reload page w/purposeful timeout to allow client setting to update
  setTimeout(() => {
    location.reload();
  }, 500);
}

/**
 * Sets the user supervisor grouping selection functionality
 *
 * @param {String} type - supervisor, alphabetical
 * @return void
 */
function modifyUserSelectionGrouping(type) {
  // set data setting
  g.clientSettingsObj.userSelectionGrouping = type;
  // update client
  updateClientSetting("USER_SELECTION_GROUPING", type);
  // reload page w/purposeful timeout to allow client setting to update
  setTimeout(() => {
    location.reload();
  }, 500);
}

/**
 * Sets whether the guided tours launcher is enabled
 *
 * @param {String} type - show, hide
 * @return void
 */
function modifyGuidedToursSelection(type) {
  // set data setting
  g.clientSettingsObj.guidedTours = type;
  // update client
  updateClientSetting("GUIDED_TOURS", type);
  // reload page w/purposeful timeout to allow client setting to update
  setTimeout(() => {
    location.reload();
  }, 500);
}

/* ------------------------------------------ Functions Notifications ------------------------------ */

// Injects base handlers
function initStylesAnimations() {
  var w, wi, hash;

  // set all zebra stripes on page load for some older IE's
  if (g.isIEVerLTE8) setZebraStripes(true);
  onResizeScrollAction();
  g.lastScrollPosition = $("body").scrollTop();

  $(window).on("resize scroll", function (event) {
    //console.log(g.onResizeScrollArray.length);
    if (typeof event.originalEvent !== "undefined") {
      if (event.type == "scroll") {
        if (g.preventScroll) $("body").scrollTop(g.lastScrollPosition);
        g.lastScrollPosition = $("body").scrollTop();
      }
      if (g.onResizeScrollID) clearTimeout(g.onResizeScrollID);
      g.onResizeScrollID = setTimeout(
        $.proxy(function () {
          // default actions
          onResizeScrollAction();
          if (event.type == "resize") {
            resizeAllMaps();
            resetOverflows();
          }
        }, this),
        500,
      );
      if (g.onResizeScrollArrayID) clearTimeout(g.onResizeScrollArrayID);
      if (g.onResizeScrollArray.length > 0) {
        g.onResizeScrollArrayID = setTimeout(function () {
          // if funcs have been added to onResizeScrollArray, loop through and call them
          for (n = 0; n < g.onResizeScrollArray.length; n++) {
            if (typeof g.onResizeScrollArray[n] === "function") g.onResizeScrollArray[n].call(event);
          }
        }, 50);
      }
    }
  });

  // default type
  var displayType = g.clientSettingsObj.numberDisplayFormat;

  // toggle decimal and human
  $("body").on("click", ".toggleHumanDecimal", function () {
    $(".dataTable .toggleToHuman, #projectTimerTable .toggleToHuman").toggleClass("hide");
    $(".dataTable .toggleToDecimal, #projectTimerTable .toggleToDecimal").toggleClass("hide");

    $("#estimate-matrix-container .toggleToHuman").toggleClass("hide"); // for the hourly timesheet top section Totals widget
    $("#estimate-matrix-container .toggleToDecimal").toggleClass("hide");

    $("#accrualBalancesTable .toggleToHuman").toggleClass("hide"); // for the hourly timesheet top section Balances widget
    $("#accrualBalancesTable .toggleToDecimal").toggleClass("hide");

    if (displayType === "decimal") {
      updateClientSetting("NUMBER_DISPLAY_FORMAT", "human");
      $(".hourlyDisplay").removeClass("inline").addClass("hide");
      $(".decimalDisplay").removeClass("hide").addClass("inline");
      displayType = "human";
    } else {
      updateClientSetting("NUMBER_DISPLAY_FORMAT", "decimal");
      $(".hourlyDisplay").removeClass("hide").addClass("inline");
      $(".decimalDisplay").removeClass("inline").addClass("hide");
      displayType = "decimal";
    }
    // keep AJAX/live updates (project timers, totals) on the same format as the toggle
    g.clientSettingsObj.numberDisplayFormat = displayType;
  });

  // tablesorter
  $("table.dataTable")
    .on(
      {
        click: function () {
          $(this).closest("tr").find("div.notesControl").trigger("click");
        },
      },
      "td.workDescription",
    )
    .on(
      {
        mouseover: function () {
          $(this).prev("tr").toggleClass("hoverRow", true);
        },
        mouseout: function () {
          $(this).prev("tr").toggleClass("hoverRow", false);
        },
      },
      "tr.notes.expanded",
    );

  // datatable
  // adds handlers for inputs and checkboxes and radio buttons
  $("#pageWrapper")
    .on(
      {
        change: function () {
          if ($(this).is("[data-group]")) {
            var $sel = $("table.group" + $(this).attr("data-group") + " input[type=checkbox]:not(:disabled)");
            if ($(this).is(":checked")) $sel.filter(":not(:checked)").prop("checked", true);
            else $sel.filter(":checked").prop("checked", false);
          }
        },
      },
      "input.checkAllInGroup",
    )
    .on(
      {
        click: function () {
          // Select All Groups / Deselect All
          if ($(this).is("[data-group]")) {
            var $sel = $("table.group" + $(this).attr("data-group") + " input[type=checkbox]:not(:disabled)");
            let isChecked = true;
            let group = [];

            if ($(this).is(".on")) {
              group = $sel.filter(":not(:checked)");
              isChecked = true;
              group.prop("checked", true);
            } else {
              isChecked = false;
              group = $sel.filter(":checked");
              group.prop("checked", false);
            }

            //  Active Users: select supervisors who report to the group just checked (e.g. check/uncheck lower level supervisors)
            if (
              group.length > 0 &&
              group[0].name == "UserList" &&
              g.clientSettingsObj.userSelection == "supervisor" &&
              g.clientSettingsObj.userSelectionGrouping == "supervisor_full"
            ) {
              // console.log("group to loop through = ", group);
              for (var i = 0; i < group.length; i++) {
                $("[name='" + group[i].name + "'][value='" + group[i].value + "']").prop("checked", isChecked);
              }
            }
          }
        },
      },
      "a.selectAllInGroup",
    )
    .on(
      {
        dblclick: function () {
          if ($.isNumeric($(this).val())) $(this).trigger("select");
        },
      },
      "input[type=text]",
    )
    .on(
      {
        change: function () {
          var on = $(this).is(":checked"),
            $inp = $(this).next("input[name=" + $(this).attr("data-name") + "]");
          $inp.val(on ? $(this).attr("data-checked-value") : $(this).attr("data-unchecked-value"));
        },
      },
      "input[data-name][data-unchecked-value]:checkbox:not([disabled])",
    )

    // ---- "Check all" box in a table was checked----
    .on(
      {
        change: function () {
          $(this)
            .closest("table")
            .find("tr > td > input:checkbox:not(:disabled)")
            .prop("checked", $(this).is(":checked"))
            .closest("table.expenseTable")
            .recalculateExpenseTotals();
        },
      },
      "table.dataTable > thead > tr > * > div > input:checkbox.checkAll",
    )
    .on(
      {
        change: function () {
          $(this).closest("table.expenseTable").recalculateExpenseTotals();
        },
      },
      "table.expenseTable tr > * > input:checkbox:not(.checkAll)",
    )

    // ---- checkbox and radio ----
    .on(
      {
        click: function () {
          if (!$(this).is(":focus")) {
            $(this).trigger("focus");
          }
        },
        focus: function () {
          var $theLabel = $("label[for=" + $(this).attr("id") + "]");
          $theLabel.data("originalColor", $theLabel.css("color"));
          if ($theLabel.closest("th").length > 0 || $theLabel.is(".onDark")) {
            $theLabel
              .stop(true, true)
              .animate({ backgroundColor: "#B0B000" }, 50)
              .animate({ backgroundColor: "#7D6D00" }, 200);
          } else {
            $theLabel.stop(true, true);
            //  Commented out color change on clicking certain elements (e.g. checkboxes on user assignment page)
            //  handle in scss instead!
            // .animate({backgroundColor:"yellow"},50)
            // .animate({backgroundColor:"#FFFF99"},200);
          }
        },
        blur: function () {
          var $theLabel = $("label[for=" + $(this).attr("id") + "]");
          $theLabel.stop(true, true).css({ backgroundColor: "transparent" });
        },
      },
      "input:checkbox,input:radio",
    )
    .on(
      {
        click: function (ev) {
          if ($(this).is("[data-disabled]")) {
            ev.preventDefault();
            ev.stopPropagation();
            ev.stopImmediatePropagation();
          }
        },
      },
      "input[type=submit],input[type=button],button",
    );

  // set error confirmation
  $("div.errorMsg, div.confirmation").each(function () {
    if (!$(this).is(":hidden")) {
      $(this).animate({ opacity: "1" }, 600);
    }
  });

  // insert explicit checkbox hidden inputs
  $("input[data-name][data-checked-value][data-unchecked-value]:checkbox:not([disabled])").each(function () {
    var $inp = $("<input />").attr({
      type: "hidden",
      name: $(this).attr("data-name"),
      value: $(this).is("[checked]") ? $(this).attr("data-checked-value") : $(this).attr("data-unchecked-value"),
    });
    $(this).after($inp);
  });

  // add click handler for buttons which change the action of a form
  // (ex: use attr data-submit-action="save" to change the input named "action" to "save"
  $("form *[data-submit-action]").on("click", function () {
    var $f = $(this).closest("form"),
      v = $(this).attr("data-submit-action");
    $f.find("input[name=Action]").val(v).end().trigger("submit");
  });

  // change table arrow direction
  $("table.showChangesInTable").showChangesInTable();
  $("table.alternateRowsByDate").each(function () {
    $(this).alternateRowsByDate();
  });
  $("tr > .autoCell").on("click", function (event) {
    if (event.target.nodeName !== "INPUT") $(this).autoCellClick();
  });

  // create ellipsis expander for divs with "more" class
  $(".more").each(function () {
    var maxChar = 80,
      str = $(this).html(),
      str1,
      str2;
    if (str.length > maxChar) {
      str1 = str.substr(0, maxChar);
      str2 = str.substr(maxChar, str.length - maxChar);
      $(this)
        .html(
          str1 +
            '<span class="moreLink">&hellip;&nbsp;<a href="javascript:void(0);">more</a></span><span class="moreContent" style="display:none;">' +
            str2 +
            "</span>",
        )
        .find("span.moreLink")
        .on("click", function () {
          $(this).siblings("span.moreContent").show(250).end().hide();
        });
    }
  });

  /**
   * Show or hide the left menu and toggle the "hamburger" icon
   */
  $("#menuExpander").on("click", function () {
    var showSideMenu = $("#contentWrapper").is(".showSideMenu");
    $(".hamburger").toggleClass("is-active");
    $("#contentWrapper").toggleClass("showSideMenu", !showSideMenu);
    initPageTop();
  });

  // If any second level menus are open in compact mode we need to close them when loading the page
  $(document).ready(function () {
    syncLeftMenuLayout();
    if ($("#sidemenu").hasClass("compact")) {
      closeAllCompactSecondaryMenus();
    }
    // If you click off the compact menu dropdown handler it will close the dropdowns (not full menu dropdowns, though)
    $(document).on("click", function (event) {
      if (!$(event.target).closest(".compact .dropdown-handler").length) {
        closeAllCompactSecondaryMenus();
      }
    });
  });

  g.onResizeScrollArray.push(function () {
    if (window.innerWidth === leftMenuLastSyncedWidth) return;
    syncLeftMenuLayout();
  });

  /**
   * Toggle the left menu between compact (icon only) and full size (icon and text)
   */
  $("#menuCompactor").on("click", function () {
    const willBeCompact = !$("#sidemenu").hasClass("compact");
    applyLeftMenuCompactMode(willBeCompact);

    const menuMode = willBeCompact ? "icon" : "standard";
    if (g.clientSettingsObj) {
      g.clientSettingsObj.menuMode = menuMode;
    }
    updateClientSetting("MENU_MODE", menuMode);
  });

  /**
   * Open and close second level menus
   */
  $(".dropdown-handler").click(function () {
    // console.log("dropdown-handler, this = ", this);
    if ($(this).hasClass("compact")) {
      compactMenuDropdown($(this));
    } else {
      standardMenuDropdown($(this));
    }
  });

  /**
   * Open and close standard (icon + text) second level menus
   * @param {Object} element - element clicked on to open the menu
   */
  function standardMenuDropdown(element) {
    element.find(".rotate").toggleClass("fa-rotate-90");
    element.next().slideToggle(250);
  }

  /**
   * Open and close compact (icon only) second level menus
   * @param {Object} element - element clicked on to open the menu
   */
  function compactMenuDropdown(element) {
    let alreadyOpen = element.find(".rotate").hasClass("down") ? true : false;

    // close (slideUp) every secondary menu
    closeAllCompactSecondaryMenus();

    // if it wasn't open, open it now
    if (!alreadyOpen) {
      element.find(".rotate").addClass("down");
      element.next().slideToggle(250);
    }
    // reposition the secondary menu next to the where we clicked
    $("#sidemenu.compact .menu-secondary").css({ top: element.position().top + "px" });
  }

  $(".dropdown-on-element-handler").click(function () {
    $(this).find(".rotate").toggleClass("down");
  });

  $(".dropdown-handler-class").click(function () {
    var target = $(this).attr("rel");
    $(this).find(".rotate").toggleClass("down");
    $("." + target).slideToggle(250);
  });

  $(".dropdown-handler-id").click(function () {
    var target = $(this).attr("data-id");
    $(this).find(".rotate").toggleClass("down");
    $("#" + target).slideToggle(250);
  });

  // fix select function focus in chrome/webkit
  if (/webkit/i.test(navigator.userAgent)) {
    HTMLInputElement.prototype.brokenSelectFunction = HTMLInputElement.prototype.select;
    HTMLInputElement.prototype.select = function () {
      setTimeout(
        (function (closureThis) {
          return function () {
            closureThis.brokenSelectFunction();
          };
        })(this),
        10,
      );
    };
  }

  // show active section in left-hand menu
  if (g.section != "") $("#" + g.section).toggleClass("active", true);

  // select text in inputs with focusSelect class
  $("input[type=text].focusSelect").on("click mouseup tap press", function () {
    this.focus();
    this.setSelectionRange(0, this.value.length);
  });

  // sanitize bad characters from text inputs and textareas
  $("input[type=text],textarea").on("paste", function (ev) {
    setTimeout(
      $.proxy(function () {
        $(this).val(
          $(this)
            .val()
            .replace(/[^\u0000-\u00FF]/g, ""),
        );
      }, this),
      1,
    );
  });

  // add -1 tab index for tooltips
  $(".ToolTip:not([tabindex])").attr("tabindex", "-1");

  // remove onclick attributes and add onclicks as event handlers
  $("input[type=button],input[type=reset],input[type=submit],input[type=image],button,a[onclick]").each(function () {
    var o,
      $t = $(this);
    if ($t.is("[onclick]")) {
      o = $t.attr("onclick");
      $t.attr("data-original-onclick", o)
        .removeAttr("onclick")
        .off("click")
        .on(
          "click",
          $.proxy(function () {
            if (!$t.is("[data-disabled]") && ($t.is("a") || !$t.closest("form").is("[data-disabled]"))) {
              //ignore:eval
              eval($t.attr("data-original-onclick"));
            }
          }, this),
        );
    }
  });
}

/**
 *	Set the Fixed top to account for watermark, error, etc. when loading or changing the side menu or settings
 */
function initPageTop() {
  // Do not adjust the header for external pages
  if ($("#externalPageWrapper")[0]) {
    return false;
  }

  let headerHeight = $("#header").height();

  // table-row display needs a set top, but not margin-top
  if ($("#contentWrapper").css("display") == "table-row") {
    $("#contentWrapper").css("margin-top", "inherit");
    $("#content").css("padding-top", headerHeight); // known iPad bug with top: or offset, so we need to force padding
    $("#sidemenu").css("padding-top", headerHeight);
    $("#footer").css("margin-top", headerHeight); // the footer needs the top margin to be expanded
  }
  // block display needs a margin-top, but not a set top
  else if ($("#contentWrapper").css("display") == "block") {
    $("#contentWrapper").css("margin-top", headerHeight);
    $("#content").css("padding-top", "initial");
    $("#sidemenu").css("padding-top", "initial");
    $("#contentWrapper").css("top", "inherit");
    $("#footer").css("top", "inherit");
    $("#footer").css("margin-top", "inherit");
  }
  return true;
}

function updateClientSetting(name, value) {
  // console.log("updateClientSetting(name,value): ", name, value);
  var url = g.baseApiUrl + "/user/client/setting";
  $.ajax({
    type: "POST",
    url: url,
    dataType: "json",
    data: {
      name: name,
      value: value,
    },
    headers: createBaseApiHeaders(getApiKeyFromStorage(), getApiTokenFromStorage()),
    cache: false,
    success: function (statusData) {
      //console.log("%c Setting client pref: ", "color:green;font-weight:bold;", name + ":" + value);
    },
    error: function (jqXHR, textStatus, errorThrown) {
      console.error("Client setting failed to update.");
      throw errorThrown;
    },
  });
}

function updateClientDisplayPref(value) {
  var url = g.baseApiUrl + "/user/client/display-preference";
  $.ajax({
    type: "POST",
    url: url,
    dataType: "json",
    data: {
      value: value,
    },
    headers: createBaseApiHeaders(getApiKeyFromStorage(), getApiTokenFromStorage()),
    cache: false,
    success: function (statusData) {
      $("#clientDisplayPrefButton").remove();
    },
    error: function (jqXHR, textStatus, errorThrown) {
      console.error("Client display setting failed to update.");
    },
  });
}

/**
 * Options Modal from Profile dropdown
 */
$(function () {
  var displayOptionsDialog;
  // old school jq ui dialog box
  displayOptionsDialog = $("#displayOptions").dialog({
    autoOpen: false,
    height: "auto",
    width: "auto",
    modal: true,
    buttons: {
      Close: function () {
        displayOptionsDialog.dialog("close");
      },
    },
  });

  $("#displayOptionsOpen").on("click", function () {
    // console.log("displayOptionsDialog = ", displayOptionsDialog);
    $("#userProfileNavMenu").hide();
    displayOptionsDialog.dialog("open");
  });
});

/**
 * Use the scrollWrapperToFit class in the parent of a tableScrollWrapper element to make the scrolling area re-adjust to the width of the page.
 * Used for scroll areas that take up pretty much the whole screen, not to be used for smaller scroll areas
 */
$(document).ready(function () {
  if ($(".scrollWrapperToFit")) {
    // console.log('scrollWrapperToFit - body width: ', $("body").width());
    setScrollerWidth();

    $(window).resize(function () {
      setScrollerWidth();
    });

    function setScrollerWidth() {
      // fit to the width of the body tag - 10%
      let adjustedWidth = $("body").width() - $("body").width() * 0.1;
      $(".scrollWrapperToFit").css("max-width", adjustedWidth + "px");
    }
  }
});

/**
 * Add a hide/show toggle to all instructions boxes: <div class="instructions"></div>
 * 	Note: adding the nocollapse class will nullify this feature e.g. <div class="instructions nocollapse"></div>
 */
$(document).ready(function () {
  let allInstructions = $(".instructions:not(.nocollapse)"); // find all the instructions boxes on a page
  for (let i = 0; i < allInstructions.length; i++) {
    let boxID = "instruct_" + i;
    $(allInstructions[i]).attr("id", boxID); // set an ID for each instructions box
    $(allInstructions[i]).wrapInner('<div class="innerInstructions"></div>'); // wrap the content in an inner div to show and hide
    // then add a toggle button at the bottom of the box
    $(allInstructions[i]).append(
      '<div class="toggleInstWrapper"><a href="##" class="icon-btn" onclick="toggleInstructions(\'' +
        boxID +
        '\')"><i id="icon_instruct_' +
        i +
        '" class="fad fa-message-minus fa-xl font-blue"></i></a></div>',
    );
  }
});

/**
 * Hide/Show instructions box when clicked
 * @param {String} instructID - ID of the instructions box (generated when the page loads)
 */
function toggleInstructions(instructID) {
  let innerInstructions = $("#" + instructID + " .innerInstructions");
  innerInstructions.toggle(); // hide show the whole instructions box
  // change icons based on the visibility of the instructions box
  if (innerInstructions.is(":visible")) {
    $("#icon_" + instructID).addClass("fa-message-minus");
    $("#icon_" + instructID).removeClass("fa-message-plus");
    $("#" + instructID).removeClass("collapsedInstruct");
  } else {
    $("#icon_" + instructID).addClass("fa-message-plus");
    $("#icon_" + instructID).removeClass("fa-message-minus");
    $("#" + instructID).addClass("collapsedInstruct"); // hides borders and removes padding from the outer instructions box
  }
}

/* ------------------------------------------ Final Load Functions ------------------------------ */

// Javascript needs to be inserted AFTER all other document ready events
// (all elements on page need to be in final hidden/shown states before this code runs) -->
// this function is called in the footer, after everything else in the document body very last document ready events, must be after others in page
function loadAfterBody() {
  $(document).ready(function () {
    setFormButtonDisableSubmitHandler();
    setAjaxFormsSubmitHandler();
    closeToolTips();
    scrollToHashAnchor();
    resetOverflows(); // must be last
  });
}
